// React
import React, { FC, useEffect, useState, memo } from 'react';

// Styling
import injectSheet from 'react-jss';
import styleSheet from './styles';

// Utilities
import { areEqual } from '../../../utils/areEqual';

interface Props {
  range: { lowerBound: number; upperBound: number };
  defaultValues: { handleOne: number; handleTwo: number };
  step: number;
  onSlideEnd?: (values: number[]) => void;
  // Used in styling
  // eslint-disable-next-line react/no-unused-prop-types
  color?: string;
  classes?: any;
  upperBoundDragLabel?: string; // Displays text while the slider is being dragged from the UpperBoundary
  lowerBoundDragLabel?: string; // Displays text while the slider is being dragged from the LowerBoundary
  upperBoundMaxLabel?: string; // Displays text while the slider is at it's highest value
  lowerBoundMaxLabel?: string; // Displays text while the slider is at it's lowest value
  valuePlacesPastTheDecimal?: number;
  ariaLabel?: string;
}

const FilterSlider: FC<Props> = (props: Props) => {
  const {
    range,
    defaultValues,
    step,
    onSlideEnd,
    classes,
    upperBoundDragLabel,
    lowerBoundDragLabel,
    upperBoundMaxLabel,
    lowerBoundMaxLabel,
    valuePlacesPastTheDecimal = 0, // Default to 0 places past the decimal,
    ariaLabel,
  } = props;

  const getPercentage = (current: number, minimum: number, maximum: number) => {
    const val = (current - minimum) / (maximum - minimum);
    const percent = val * 100;
    return percent;
  };

  const [values, setValues] = useState(defaultValues);
  const [labels, setLabels] = useState(defaultValues);
  const [percentages, setPercentages] = useState({
    handleOne: getPercentage(
      defaultValues.handleOne,
      range.lowerBound,
      range.upperBound
    ),
    handleTwo: getPercentage(
      defaultValues.handleTwo,
      range.lowerBound,
      range.upperBound
    ),
  });

  useEffect(() => {
    setValues(defaultValues);
    setLabels(defaultValues);
    setPercentages({
      handleOne: getPercentage(
        defaultValues.handleOne,
        range.lowerBound,
        range.upperBound
      ),
      handleTwo: getPercentage(
        defaultValues.handleTwo,
        range.lowerBound,
        range.upperBound
      ),
    });
  }, [defaultValues]);

  const getLowerHandle = (handles: {
    handleOne: number;
    handleTwo: number;
  }) => {
    if (handles.handleOne < handles.handleTwo) {
      return handles.handleOne;
    }
    return handles.handleTwo;
  };

  const getUpperHandle = (handles: {
    handleOne: number;
    handleTwo: number;
  }) => {
    if (handles.handleTwo > handles.handleOne) {
      return handles.handleTwo;
    }
    return handles.handleOne;
  };

  const handleSliderChange = (event: any) => {
    const newValues = {
      ...values,
      [event.target.id]: Number(event.target.value),
    };
    setValues(newValues);

    const newPercentages = {
      ...percentages,
      [event.target.id]: getPercentage(
        Number(event.target.value),
        range.lowerBound,
        range.upperBound
      ),
    };
    setPercentages(newPercentages);

    setLabels({
      handleOne: getLowerHandle(newValues),
      handleTwo: getUpperHandle(newValues),
    });
  };

  const handleSlideEnd = () => {
    setValues(labels);
    setPercentages({
      handleOne: getPercentage(
        labels.handleOne,
        range.lowerBound,
        range.upperBound
      ),
      handleTwo: getPercentage(
        labels.handleTwo,
        range.lowerBound,
        range.upperBound
      ),
    });
    onSlideEnd([labels.handleOne, labels.handleTwo]);
  };

  const handleKeyUp = (event: any) => {
    if (event.keyCode === 37 || event.keyCode === 39) {
      handleSlideEnd();
    }
  };

  return (
    <div className={classes.filterSliderContainer} data-testid="slider">
      <div className={classes.values}>
        {`${labels.handleOne.toFixed(valuePlacesPastTheDecimal)}${
          labels.handleOne === range.lowerBound
            ? lowerBoundMaxLabel || lowerBoundDragLabel || ''
            : lowerBoundDragLabel || ''
        } - ${labels.handleTwo.toFixed(valuePlacesPastTheDecimal)}${
          labels.handleTwo === range.upperBound
            ? upperBoundMaxLabel || upperBoundDragLabel || ''
            : upperBoundDragLabel || ''
        }`}
      </div>
      <div className={classes.sliderContainer}>
        <input
          type="range"
          min={range.lowerBound}
          max={range.upperBound}
          value={values.handleOne}
          className={classes.slider}
          onChange={handleSliderChange}
          onMouseUp={handleSlideEnd}
          onKeyUp={handleKeyUp}
          step={step}
          id="handleOne"
          aria-label={ariaLabel}
          data-testid="handleOne"
        />
      </div>
      <div className={classes.sliderRange} />
      <div className={classes.sliderContainer}>
        <input
          type="range"
          min={range.lowerBound}
          max={range.upperBound}
          value={values.handleTwo}
          className={classes.slider}
          onChange={handleSliderChange}
          onMouseUp={handleSlideEnd}
          onKeyUp={handleKeyUp}
          step={step}
          id="handleTwo"
          aria-label={ariaLabel}
          data-testid="handleTwo"
        />
      </div>
      <div className={classes.railContainer}>
        <div
          className={classes.inactiveRailRight}
          style={{
            width: `${100 - getUpperHandle(percentages)}%`,
          }}
        />
        <div
          className={classes.inactiveRailLeft}
          style={{
            width: `${getLowerHandle(percentages)}%`,
          }}
        />
        <div className={classes.activeRail} data-testid="activeRail" />
      </div>
    </div>
  );
};

FilterSlider.defaultProps = {
  onSlideEnd: () => {},
  color: undefined,
  classes: {},
  upperBoundDragLabel: undefined,
  lowerBoundDragLabel: undefined,
  upperBoundMaxLabel: undefined,
  lowerBoundMaxLabel: undefined,
  valuePlacesPastTheDecimal: undefined,
  ariaLabel: undefined,
};

export default injectSheet(styleSheet)(memo(FilterSlider, areEqual));
